/* eslint-disable react-hooks/exhaustive-deps */
import React, { FC, useCallback, useEffect, useState } from 'react';

import { Dictionary } from 'lodash';

import {
	AzureIssueCreatorFormUI,
	IAzureFieldType,
	IAzureProject,
	OptionType
} from '@evinced-private/ui-common';

import {
	createIssue,
	getOrganizations,
	getProjectFields,
	getProjects,
	getProjectTypes
} from '../../api/AzureApi';
import { asyncHandler } from '../../helpers/AsyncHelper';
import { useConfiguration } from '../../providers/configurationProvider/ConfigurationProvider';
import { SiteScannerUiToggles } from '../../services/LocalTogglesService';
import { logger } from '../../services/Logger';
import { ScreenshotService } from '../../services/ScreenshotService';
import { Report } from '../../types/ReportModel';
import { SiteScannerPopup } from '../common/site-scanner-popup/SiteScannerPopup';

import {
	getBaseUrl,
	getOverrideFields,
	replaceProjectName,
	updateFieldsFromIssueData
} from './Helpers/AzureHelper';

import './AzureIssueCreator.scss';

const alertPopupTitle = 'Connect to Azure';
const issueFormTitle = 'Create Issue in Azure';

const fieldsToIgnore = ['Iteration ID', 'Area ID'];

interface IAzureIssueCreatorProps {
	isOpen: boolean;
	closeModal: () => void;
	ticketData: Report;
}

const AzureIssueCreator: FC<IAzureIssueCreatorProps> = ({
	closeModal,
	isOpen,
	ticketData
}: IAzureIssueCreatorProps): JSX.Element => {
	const { getToggle } = useConfiguration();
	const bypassAzureProcessRules = !getToggle(
		SiteScannerUiToggles.DISABLE_AZURE_BYPASS_PROCESS_RULES
	);
	const [isLoading, setLoading] = useState<boolean>(true);
	const [host, setHost] = useState<string>(null);
	const [alertModalOpen, setAlertModalOpen] = useState(false);
	const [projects, setProjects] = useState<IAzureProject[]>([]);
	const [issueTypes, setIssueTypes] = useState<OptionType[]>([]);
	const [selectedProject, setSelectedProject] = useState<OptionType[]>([]);
	const [selectedIssueType, setSelectedIssueType] = useState<OptionType>(null);
	const [fields, setFields] = useState<Dictionary<IAzureFieldType>>(null);
	const [screenshotUrl, setScreenshotUrl] = useState<string>('');

	const updateField = useCallback(
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		(newValue: any, field: IAzureFieldType): void => {
			const updatedField =
				typeof newValue === 'object'
					? { ...field, value: newValue.value }
					: { ...field, value: newValue };

			setFields((prevFields) => ({ ...prevFields, [field.referenceName]: updatedField }));
		},
		[]
	);

	const updateIssueData = async (newFields: Dictionary<IAzureFieldType>): Promise<void> => {
		try {
			if (ticketData) {
				const boundingBoxes = ticketData.elements.map((element) => element.boundingBox);
				const [screenshotImageData] = await asyncHandler(
					ScreenshotService.getFullPageScreenshotWithIssues({
						pageScreenshotUrl: ticketData.pageScreenshot,
						boundingBoxes
					})
				);

				const updatedFields: Dictionary<IAzureFieldType> = updateFieldsFromIssueData(
					newFields,
					ticketData
				);

				setFields(updatedFields);

				if (screenshotImageData) {
					setScreenshotUrl(screenshotImageData as string);
				}
			}
		} catch (error) {
			logger.error(
				`Error getting information regarding updateIssueData with error: ${error} with host: ${host}`
			);
		}
	};

	const checkIfLoggedIn = async (): Promise<void> => {
		try {
			const formattedOrganizations: OptionType[] = await getOrganizations();

			if (formattedOrganizations.length) {
				setAlertModalOpen(false);

				const organizationUrl = `${getBaseUrl()}/${formattedOrganizations[0].label}`;
				const formattedProjects: IAzureProject[] = await getProjects(organizationUrl);
				const formattedSelectedProject =
					formattedProjects?.length > 1
						? formattedProjects.filter((project) => project.name === formattedProjects[0].label)[0]
						: formattedProjects[0];
				const formattedHost = `${organizationUrl}/${formattedSelectedProject.label}`;

				setProjects(formattedProjects);
				setSelectedProject([formattedSelectedProject]);
				setHost(formattedHost);
			} else {
				setAlertModalOpen(true);
			}
		} catch (error) {
			logger.error(
				`Error getting information regarding fetchData with error: ${error} with host: ${host}`
			);
		}
	};

	const fetchData = async (): Promise<void> => {
		try {
			if (host) {
				const issueTypes: OptionType[] = await getProjectTypes(host);
				const formattedProjects: IAzureProject[] = await getProjects(host);
				const formattedFields: Dictionary<IAzureFieldType> = await getProjectFields(
					host,
					issueTypes[0]?.label
				);

				setFields(formattedFields);
				setIssueTypes(issueTypes);
				setProjects(formattedProjects);
				setSelectedIssueType(issueTypes[0]);
				updateIssueData(formattedFields);
			}
		} catch (error) {
			logger.error(
				`Error getting information regarding fetchData with error: ${error} with host: ${host}`
			);
		} finally {
			setLoading(false);
		}
	};

	const initApi = useCallback(async (): Promise<void> => {
		if (host) {
			try {
				setLoading(true);
				await fetchData();
			} catch (error) {
				logger.error(
					`Error getting information regarding initApi with error: ${error} with host: ${host}`
				);
			}
		}
	}, [host]);

	useEffect(() => {
		checkIfLoggedIn();
	}, []);

	useEffect(() => {
		initApi();
	}, [initApi]);

	const onChangeProjectPicker = async (newProject: OptionType): Promise<void> => {
		const updatedUrl = replaceProjectName(host, newProject.label);
		try {
			setHost(updatedUrl);
			setLoading(true);
			setSelectedProject([newProject]);
			updateIssueData(fields);
		} catch (error) {
			logger.error(
				`Error getting information regarding onChangeProjectPicker with error: ${error} with host: ${updatedUrl}`
			);
		} finally {
			setLoading(false);
		}
	};

	const onChangeIssueTypePicker = async (
		newIssueType: OptionType,
		previousFields: Dictionary<IAzureFieldType>
	): Promise<void> => {
		setLoading(true);
		try {
			const newFields: Dictionary<IAzureFieldType> = await getProjectFields(
				host,
				newIssueType.label
			);
			const overrideFields: Dictionary<IAzureFieldType> = getOverrideFields(
				newFields,
				previousFields
			);

			setSelectedIssueType(newIssueType);
			updateIssueData(overrideFields);
			setLoading(false);
		} catch (error) {
			logger.error(
				`Error getting information regarding onChangeIssueTypePicker with error: ${error} with host: ${host}`
			);
		} finally {
			setLoading(false);
		}
	};

	// publish button should be disabled as long as one of the required fields is not full
	const isPublishButtonDisabled = (): boolean => {
		if (fields) {
			const isDisabled = Object.keys(fields).some((fieldKey) => {
				const field = fields[fieldKey];

				// so the button should be disabled in such case
				if (field.alwaysRequired && !field.value && !fieldsToIgnore.includes(field.name)) {
					return true;
				}
				return field.alwaysRequired ? !fields[fieldKey] : false;
			});
			return isDisabled;
		}
		return true;
	};

	const onCreateIssueClicked = async (): Promise<void> => {
		try {
			setLoading(true);
			const shouldCloseModal = await createIssue(
				host,
				selectedIssueType.label,
				screenshotUrl,
				fields,
				bypassAzureProcessRules
			);

			if (shouldCloseModal) {
				closeModal();
			}
		} catch (error) {
			logger.error(
				`Error getting information regarding createIssue with error: ${error} with host: ${host}`
			);
		} finally {
			setLoading(false);
		}
	};

	const buttons = [
		{
			title: 'OK',
			onClick: () => {
				logger.info('Error popup closed on OK click');
				closeModal();
			}
		}
	];

	if (!host && alertModalOpen) {
		return (
			<SiteScannerPopup
				title={alertPopupTitle}
				buttons={buttons}
				isControlled
				isCentered
				isOpen={alertModalOpen}
				className="error-popup"
				onClose={closeModal}
				popupWidth="100%"
			>
				<div className="alert-popup">Azure is not connected</div>
			</SiteScannerPopup>
		);
	}

	return (
		<SiteScannerPopup
			title={issueFormTitle}
			isOpen={isOpen}
			popupWidth="100%"
			className="azure-popup"
			isBgTransparent
			isControlled
			onClose={closeModal}
		>
			<AzureIssueCreatorFormUI
				title=""
				isLoading={isLoading}
				projects={projects}
				selectedProject={selectedProject}
				issueTypes={issueTypes}
				selectedIssueType={selectedIssueType}
				fields={fields}
				screenshotUrl={screenshotUrl}
				onChangeProjectPicker={onChangeProjectPicker}
				onChangeIssueTypePicker={onChangeIssueTypePicker}
				updateField={updateField}
				isPublishButtonDisabled={isPublishButtonDisabled}
				onCreateIssueClicked={onCreateIssueClicked}
				closeModal={closeModal}
			/>
		</SiteScannerPopup>
	);
};

export { AzureIssueCreator };
